import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { Trash2, Plus, Minus } from 'lucide-react';
import { motion } from 'framer-motion';

const Cart = () => {
  const { cart, updateCart, removeFromCart } = useCart();
  const navigate = useNavigate();

  const updateQuantity = async (item, newQuantity) => {
    if (newQuantity < 1) {
      await removeFromCart(item.product_id, item.variant_index);
      return;
    }
    
    const updatedItems = cart.items.map(cartItem => {
      if (cartItem.product_id === item.product_id && cartItem.variant_index === item.variant_index) {
        return { ...cartItem, quantity: newQuantity };
      }
      return cartItem;
    });
    
    await updateCart(updatedItems);
  };

  const getSubtotal = () => {
    return cart.items?.reduce((total, item) => {
      const price = item.product?.base_price || 0;
      return total + (price * item.quantity);
    }, 0) || 0;
  };

  const subtotal = getSubtotal();
  
  // Tiered shipping calculation
  const calculateShipping = (orderTotal) => {
    if (orderTotal >= 75) return 0;        // FREE shipping
    if (orderTotal >= 30) return 7.99;     // $30-$74.99
    if (orderTotal >= 10) return 4.99;     // $10-$29.99
    return 2.99;                            // Below $10
  };
  
  const shipping = calculateShipping(subtotal);
  const tax = subtotal * 0.0725;
  const total = subtotal + shipping + tax;

  if (!cart.items || cart.items.length === 0) {
    return (
      <div className="min-h-screen bg-white" data-testid="empty-cart">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <h1
              className="text-4xl font-bold mb-4"
              style={{ fontFamily: 'Playfair Display, serif' }}
              data-testid="empty-cart-title"
            >
              Your Cart is Empty
            </h1>
            <p className="text-gray-600 mb-8">Add some items to get started!</p>
            <Link
              to="/shop"
              className="inline-block bg-black text-white py-3 px-8 rounded-full hover:bg-gray-800 transition-colors"
              data-testid="continue-shopping-button"
            >
              Continue Shopping
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="cart-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1
          className="text-4xl font-bold mb-8"
          style={{ fontFamily: 'Playfair Display, serif' }}
          data-testid="cart-title"
        >
          Shopping Cart
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4" data-testid="cart-items">
            {cart.items.map((item, idx) => {
              const product = item.product;
              if (!product) return null;
              
              const variant = product.variants[item.variant_index];
              const price = product.base_price;

              return (
                <motion.div
                  key={`${item.product_id}-${item.variant_index}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="flex gap-4 bg-[#F9F9F9] p-4 rounded-lg"
                  data-testid={`cart-item-${idx}`}
                >
                  <Link to={`/product/${product.id}`} className="flex-shrink-0">
                    <img
                      src={product.images[0] || 'https://via.placeholder.com/150'}
                      alt={product.name}
                      className="w-24 h-24 object-cover rounded"
                      data-testid={`cart-item-image-${idx}`}
                    />
                  </Link>

                  <div className="flex-1">
                    <Link to={`/product/${product.id}`}>
                      <h3 className="font-medium mb-1 hover:text-[#D4AF37] transition-colors" data-testid={`cart-item-name-${idx}`}>
                        {product.name}
                      </h3>
                    </Link>
                    {variant && (
                      <p className="text-sm text-gray-600 mb-2" data-testid={`cart-item-variant-${idx}`}>
                        {variant.size && `Size: ${variant.size}`}
                        {variant.size && variant.color && ' • '}
                        {variant.color && `Color: ${variant.color}`}
                      </p>
                    )}
                    <p className="font-semibold" data-testid={`cart-item-price-${idx}`}>${price.toFixed(2)}</p>
                  </div>

                  <div className="flex flex-col items-end justify-between">
                    <button
                      onClick={() => removeFromCart(item.product_id, item.variant_index)}
                      className="text-red-600 hover:text-red-700 transition-colors"
                      data-testid={`remove-item-${idx}`}
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>

                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => updateQuantity(item, item.quantity - 1)}
                        className="w-8 h-8 border border-gray-300 rounded hover:bg-white transition-colors flex items-center justify-center"
                        data-testid={`decrease-quantity-${idx}`}
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="w-8 text-center font-medium" data-testid={`item-quantity-${idx}`}>{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item, item.quantity + 1)}
                        className="w-8 h-8 border border-gray-300 rounded hover:bg-white transition-colors flex items-center justify-center"
                        data-testid={`increase-quantity-${idx}`}
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-[#F9F9F9] p-6 rounded-lg sticky top-24" data-testid="order-summary">
              <h2 className="text-xl font-bold mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                Order Summary
              </h2>

              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-sm">
                  <span>Subtotal</span>
                  <span data-testid="subtotal-value">${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Shipping</span>
                  <span data-testid="shipping-value">
                    {shipping === 0 ? 'FREE' : `$${shipping.toFixed(2)}`}
                  </span>
                </div>
                {/* Shipping savings message */}
                {shipping === 0 ? (
                  <p className="text-xs text-green-600 font-medium">You qualify for FREE shipping!</p>
                ) : subtotal < 10 ? (
                  <p className="text-xs text-gray-500">Add ${(10 - subtotal).toFixed(2)} more to reduce shipping to $4.99</p>
                ) : subtotal < 30 ? (
                  <p className="text-xs text-gray-500">Add ${(30 - subtotal).toFixed(2)} more to reduce shipping to $7.99</p>
                ) : subtotal < 75 ? (
                  <p className="text-xs text-[#D4AF37] font-medium">Add ${(75 - subtotal).toFixed(2)} more for FREE shipping!</p>
                ) : null}
                <div className="flex justify-between text-sm">
                  <span>Tax</span>
                  <span data-testid="tax-value">${tax.toFixed(2)}</span>
                </div>
                <div className="border-t pt-3 flex justify-between font-bold text-lg">
                  <span>Total</span>
                  <span data-testid="total-value">${total.toFixed(2)}</span>
                </div>
              </div>

              <button
                onClick={() => navigate('/checkout')}
                className="w-full bg-[#D4AF37] text-white py-3 rounded-full hover:bg-[#B5952F] transition-colors font-medium"
                data-testid="checkout-button"
              >
                Proceed to Checkout
              </button>

              {/* Trust Badges */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex items-center justify-center gap-2 mb-3">
                  <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  <span className="text-xs text-gray-600 font-medium">Secure SSL Encrypted Checkout</span>
                </div>
                <div className="flex items-center justify-center gap-3 flex-wrap">
                  {/* Visa */}
                  <div className="bg-white rounded px-2 py-1 border border-gray-200">
                    <svg className="h-4 w-auto" viewBox="0 0 780 500" fill="none">
                      <rect width="780" height="500" rx="40" fill="#1A1F71"/>
                      <path d="M293.2 348.7l33.4-195h53.4l-33.4 195h-53.4zm213.4-190.4c-10.5-4-27-8.3-47.5-8.3-52.4 0-89.4 26.3-89.7 63.9-.3 27.8 26.4 43.3 46.5 52.6 20.7 9.5 27.6 15.5 27.5 24-.1 13-16.5 18.9-31.7 18.9-21.2 0-32.5-2.9-49.9-10.2l-6.8-3.1-7.4 43.1c12.4 5.4 35.3 10.1 59.1 10.3 55.8 0 92-26 92.5-66.2.2-22.1-14-38.9-44.7-52.8-18.6-9-30-15-29.9-24.2.1-8.1 9.6-16.8 30.4-16.8 17.4-.3 30 3.5 39.8 7.4l4.8 2.2 7.2-41.9m136.6-4.9h-41c-12.7 0-22.2 3.5-27.8 16.1l-78.8 177.8h55.7s9.1-23.9 11.2-29.1h68.1c1.6 6.8 6.5 29.1 6.5 29.1h49.3l-43.2-193.9zm-65.6 125.1c4.4-11.2 21.2-54.3 21.2-54.3-.3.5 4.4-11.3 7.1-18.6l3.6 16.8s10.2 46.3 12.3 56.1h-44.2zM285 153.3l-51.9 133-5.5-26.7c-9.6-30.7-39.6-64-73.1-80.6l47.5 169h56.2l83.6-195h-56.8z" fill="white"/>
                    </svg>
                  </div>
                  {/* Mastercard */}
                  <div className="bg-white rounded px-2 py-1 border border-gray-200">
                    <svg className="h-4 w-auto" viewBox="0 0 780 500" fill="none">
                      <rect width="780" height="500" rx="40" fill="white"/>
                      <circle cx="299" cy="250" r="158" fill="#EB001B"/>
                      <circle cx="481" cy="250" r="158" fill="#F79E1B"/>
                      <path d="M390 117c47.4 35.3 78 91.8 78 155s-30.6 119.7-78 155c-47.4-35.3-78-91.8-78-155s30.6-119.7 78-155z" fill="#FF5F00"/>
                    </svg>
                  </div>
                  {/* Apple Pay */}
                  <div className="bg-black rounded px-2 py-1">
                    <svg className="h-4 w-auto" viewBox="0 0 780 500" fill="none">
                      <rect width="780" height="500" rx="40" fill="black"/>
                      <path d="M234.6 150.5c13.2-16.6 22.1-39.7 19.7-62.7-19 .8-42 12.7-55.6 28.7-12.2 14.1-22.9 36.8-20 58.5 21.2 1.6 42.9-10.8 55.9-24.5zm19.5 31.1c-31-.1.8-43.4 63.5-45.4 1.2-11.4-3.1-22.6-10.1-30.9-9.4-11.1-24.3-17.6-38.8-17.6-24.4 0-35.8 11.7-53.2 11.7-18 0-32.1-11.5-52.9-11.5-27.3.4-56.9 22.2-71.2 60-20.1 53.1-5.2 131.4 35.5 174.4 14.5 15.3 32.1 32.5 55.6 31.9 21.4-.8 29.5-13.8 55.4-13.8 25.8 0 32.6 13.8 55.5 13.3 24.1-.4 39.7-15.5 54.2-30.8 10.5-12.1 14.4-18.2 22.8-32-59.9-22.8-69.5-108.3-10.3-141.3z" fill="white"/>
                      <path d="M458.7 347.7V159.9h52.5c38.5 0 63.5 24.7 63.5 62.3 0 37.7-25.6 62.5-64.6 62.5h-32.2v63h-19.2zm19.2-79.3h27.4c26.8 0 42.1-14.5 42.1-44.3 0-29.7-15.3-44.1-42-44.1h-27.5v88.4zm120.5 81.5c0-19.6 15-31.6 41.6-33.1l30.7-1.8v-8.7c0-12.4-8.4-19.8-22.4-19.8-13.2 0-21.5 6.6-23.5 17h-17.6c1.3-18.8 16.9-33.4 41.6-33.4 24.4 0 40.4 13.5 40.4 34.2v71.4h-17.6v-17h-.4c-5.2 11.4-16.8 18.4-31 18.4-19.3 0-31.8-12-31.8-27.2zm72.3-8.2v-8.9l-27.7 1.7c-13.8.8-21.6 7.2-21.6 17.1 0 9.8 8.2 16.2 20.7 16.2 16.2 0 28.6-10.8 28.6-26.1zm46.5 49.5l-1.7-14.6c2.1.6 6.9.8 8.9.8 7.1 0 10.9-3 13.2-10.5l1.4-4.5-37.2-101.5h19.8l27 84.5h.4l27-84.5h19.3l-38.6 106.6c-9 24.5-19.3 32.6-41 32.6-2 0-6.7-.2-8.5-.9z" fill="white"/>
                    </svg>
                  </div>
                  {/* Google Pay */}
                  <div className="bg-white rounded px-2 py-1 border border-gray-200">
                    <svg className="h-4 w-auto" viewBox="0 0 780 500" fill="none">
                      <rect width="780" height="500" rx="40" fill="white"/>
                      <path d="M390.3 238.6v47.8h-15.1V190h40.1c10 0 18.5 3.3 25.5 10s10.5 15 10.5 24.6c0 9.9-3.5 18-10.5 24.6s-15.4 9.9-25.5 9.9h-25zm0-13.4h25.1c5.7 0 10.5-1.9 14.4-5.6 3.9-3.8 5.9-8.4 5.9-14 0-5.4-2-10-5.9-13.8-3.9-3.8-8.7-5.7-14.4-5.7h-25.1v39.1z" fill="#5F6368"/>
                      <path d="M497.1 219c11 0 19.7 2.9 26.1 8.8 6.4 5.9 9.6 13.9 9.6 24v56.5h-14.5v-12.7h-.7c-6.2 10.4-14.4 15.6-24.8 15.6-8.8 0-16.2-2.6-22.1-7.9-5.9-5.3-8.9-11.9-8.9-20 0-8.4 3.2-15.1 9.5-20 6.3-4.9 14.8-7.4 25.5-7.4 9.1 0 16.6 1.7 22.5 5v-3.5c0-5.4-2.1-10-6.2-13.6-4.1-3.7-8.9-5.5-14.5-5.5-8.4 0-15 3.6-19.9 10.7l-13.4-8.4c7.3-10.6 18.1-15.6 32.8-15.6zm-19.5 59.9c0 4.1 1.7 7.5 5.2 10.3 3.5 2.8 7.6 4.2 12.3 4.2 6.6 0 12.4-2.4 17.3-7.3 4.9-4.9 7.3-10.6 7.3-17.3-4.8-3.8-11.4-5.7-19.9-5.7-6.2 0-11.4 1.5-15.5 4.5-4.2 3-6.7 6.7-6.7 11.3z" fill="#5F6368"/>
                      <path d="M616.6 221.9l-50.9 117h-15.5l18.9-41-33.5-76h16.4l24.3 58.6h.3l23.7-58.6h16.3z" fill="#5F6368"/>
                      <path d="M248.2 251.9c0-5-0.4-9.8-1.2-14.5h-67v27.5h38.4c-1.6 8.9-6.5 16.4-14 21.5v17.8h22.6c13.3-12.2 20.9-30.2 20.9-52.3z" fill="#4285F4"/>
                      <path d="M180 308c18.9 0 34.8-6.3 46.4-17l-22.6-17.8c-6.3 4.2-14.3 6.7-23.8 6.7-18.3 0-33.8-12.4-39.4-29h-23.3v18.4c11.5 22.8 35.1 38.7 62.7 38.7z" fill="#34A853"/>
                      <path d="M140.6 250.9c-1.4-4.2-2.2-8.7-2.2-13.4s.8-9.2 2.2-13.4v-18.4h-23.3c-4.7 9.4-7.4 20-7.4 31.8s2.7 22.4 7.4 31.8l23.3-18.4z" fill="#FBBC04"/>
                      <path d="M180 195.2c10.3 0 19.6 3.5 26.9 10.5l20.1-20.1c-12.2-11.4-28.1-18.4-47-18.4-27.6 0-51.2 15.9-62.7 38.7l23.3 18.4c5.6-16.6 21.1-29.1 39.4-29.1z" fill="#EA4335"/>
                    </svg>
                  </div>
                </div>
              </div>

              <Link
                to="/shop"
                className="block text-center text-sm mt-4 hover:text-[#D4AF37] transition-colors"
                data-testid="continue-shopping-link"
              >
                Continue Shopping
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
